import { IMeshData } from "@pageBuilder/typings";
import { CssProperties } from "../../models/css-property";
import { VerticalAlignmentRenderer } from "src/widgets/renderers/vertical-alignment-renderer";
import { MeshColumnRenderer } from "./mesh-column-renderer";
import { CascadeDataCleanUpService } from "src/services/cascade-data-cleanup.service";
import { ElementStyleService } from "../../services/element-style.service";

export class MeshRenderer {

    private readonly verticalAlignmentRenderer: VerticalAlignmentRenderer;
    private readonly columnRenderer: MeshColumnRenderer;
    private readonly styleService: ElementStyleService;

    constructor() {
        this.verticalAlignmentRenderer = new VerticalAlignmentRenderer();
        this.columnRenderer = new MeshColumnRenderer();
        this.styleService = new ElementStyleService();
    }

    public createNew(): HTMLElement {
        const mesh = document.createElement("div");
        mesh.setAttribute("data-mesh", "");

        const container = document.createElement("div");
        container.setAttribute("data-mesh-container", "");
        container.classList.add("mesh-container");
        mesh.append(container);

        return mesh;
    }

    public render(element: HTMLElement, data: IMeshData): HTMLElement {
        const cleanData = CascadeDataCleanUpService.cleanUp(data);
        element.setAttribute("data-element-json", JSON.stringify(cleanData));

        if (data.id) {
            element.id = data.id;
        }

        if (data.meshAlignment && element?.id) {
            const justifyContent = this.verticalAlignmentRenderer.getAlignmentBySelfPosition(data.meshAlignment);

            const containerProps = new CssProperties();
            containerProps.tryAdd("justify-content", justifyContent);
            this.styleService.setStyle(element, `#${element.id} .mesh-container`, containerProps.data);
            this.styleService.setStyle(element, `#${element.id} .mesh-container .mesh-row .col`, containerProps.data);
        }

        return element;
    }

    public rerenderColumns(element: HTMLElement): void {
        const columns = element.querySelectorAll<HTMLElement>(".mesh-container .mesh-row .col");

        columns.forEach(column => {
            const data = this.columnRenderer.getData(column);
            this.columnRenderer.render(column, data);
        });
    }

    public getData(element: HTMLElement): IMeshData {
        const parsedData = JSON.parse(element.getAttribute("data-element-json")!) as IMeshData;
        return CascadeDataCleanUpService.cleanUp(parsedData);
    }

    public getContentSelectorsInternal(element: HTMLElement): Array<string> {
        return [`#${element.id}`, `#${element.id} .mesh-container`, `#${element.id} .mesh-container .mesh-row .col`];
    }

    public cleanup(element: HTMLElement): void {
        this.styleService.clearStyle(element, `#${element.id} .mesh-container`);
        this.styleService.clearStyle(element, `#${element.id} .mesh-container .mesh-row .col`);
        element.remove();
    }

}
